/**
 * Paleta de colores del tema de la aplicación.
 * Define de forma centralizada primary, secondary, tertiary, success, warning, danger, light, dark y medium.
 * Convención Ionic: cada color tiene base, contrast, shade y tint.
 * Se aplican al documento como variables CSS --ion-color-*.
 */

export type ClaveColorTema =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'light'
  | 'medium'
  | 'dark';

export interface ColorTemaDef {
  clave: ClaveColorTema;
  nombre: string;
  hex: string;
  rgb: string;
  contrast: string;
  contrastRgb: string;
  shade: string;
  tint: string;
}

/** Paleta por defecto del tema (alineada con Ionic y con identidad INDAP). */
export const PALETA_TEMA: Record<ClaveColorTema, ColorTemaDef> = {
  primary: {
    clave: 'primary',
    nombre: 'Primario',
    hex: '#0F69C4',
    rgb: '15, 105, 196',
    contrast: '#ffffff',
    contrastRgb: '255, 255, 255',
    shade: '#0D5EAF',
    tint: '#2780D1',
  },
  secondary: {
    clave: 'secondary',
    nombre: 'Secundario',
    hex: '#3dc2ff',
    rgb: '61, 194, 255',
    contrast: '#ffffff',
    contrastRgb: '255, 255, 255',
    shade: '#36abe0',
    tint: '#50c8ff',
  },
  tertiary: {
    clave: 'tertiary',
    nombre: 'Terciario',
    hex: '#5260ff',
    rgb: '82, 96, 255',
    contrast: '#ffffff',
    contrastRgb: '255, 255, 255',
    shade: '#4854e0',
    tint: '#6370ff',
  },
  success: {
    clave: 'success',
    nombre: 'Éxito',
    hex: '#2dd36f',
    rgb: '45, 211, 111',
    contrast: '#ffffff',
    contrastRgb: '255, 255, 255',
    shade: '#28ba62',
    tint: '#42d77d',
  },
  warning: {
    clave: 'warning',
    nombre: 'Advertencia',
    hex: '#ffc409',
    rgb: '255, 196, 9',
    contrast: '#000000',
    contrastRgb: '0, 0, 0',
    shade: '#e0ac08',
    tint: '#ffca22',
  },
  danger: {
    clave: 'danger',
    nombre: 'Peligro',
    hex: '#eb445a',
    rgb: '235, 68, 90',
    contrast: '#ffffff',
    contrastRgb: '255, 255, 255',
    shade: '#cf3c4f',
    tint: '#ed576b',
  },
  light: {
    clave: 'light',
    nombre: 'Claro',
    hex: '#f4f5f8',
    rgb: '244, 245, 248',
    contrast: '#000000',
    contrastRgb: '0, 0, 0',
    shade: '#d6d6da',
    tint: '#f5f6f9',
  },
  medium: {
    clave: 'medium',
    nombre: 'Medio',
    hex: '#92949c',
    rgb: '146, 148, 156',
    contrast: '#ffffff',
    contrastRgb: '255, 255, 255',
    shade: '#808289',
    tint: '#9d9fa6',
  },
  dark: {
    clave: 'dark',
    nombre: 'Oscuro',
    hex: '#222428',
    rgb: '34, 36, 40',
    contrast: '#ffffff',
    contrastRgb: '255, 255, 255',
    shade: '#1e2023',
    tint: '#383a3e',
  },
};

/**
 * Devuelve la definición de un color del tema.
 */
export function obtenerColorTemaDef(clave: ClaveColorTema): ColorTemaDef {
  return PALETA_TEMA[clave];
}

/**
 * Aplica todas las variables CSS de Ionic de la paleta al documento.
 * Útil al iniciar la app para que secondary, tertiary, etc. usen los valores definidos aquí.
 * El color primary puede ser sobrescrito después por la preferencia del usuario (colores-primarios.config).
 */
export function aplicarPaletaTemaEnDocumento(): void {
  const root = document.documentElement;
  (Object.keys(PALETA_TEMA) as ClaveColorTema[]).forEach((clave) => {
    const def = PALETA_TEMA[clave];
    root.style.setProperty(`--ion-color-${clave}`, def.hex);
    root.style.setProperty(`--ion-color-${clave}-rgb`, def.rgb);
    root.style.setProperty(`--ion-color-${clave}-contrast`, def.contrast);
    root.style.setProperty(`--ion-color-${clave}-contrast-rgb`, def.contrastRgb);
    root.style.setProperty(`--ion-color-${clave}-shade`, def.shade);
    root.style.setProperty(`--ion-color-${clave}-tint`, def.tint);
  });
}

/**
 * Aplica solo el color primary de la paleta al documento.
 * Usado cuando se quiere resetear el primary al valor de la paleta (p. ej. tema oscuro).
 */
export function aplicarPrimaryPaletaEnDocumento(): void {
  const def = PALETA_TEMA.primary;
  const root = document.documentElement;
  root.style.setProperty('--ion-color-primary', def.hex);
  root.style.setProperty('--ion-color-primary-rgb', def.rgb);
  root.style.setProperty('--ion-color-primary-contrast', def.contrast);
  root.style.setProperty('--ion-color-primary-contrast-rgb', def.contrastRgb);
  root.style.setProperty('--ion-color-primary-shade', def.shade);
  root.style.setProperty('--ion-color-primary-tint', def.tint);
}
